import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData } from "react-router";
import ContactSection from "~/components/contact/ContactSection";
import TempleLocations from "~/components/contact/TempleLocations";
import { OrganizationSchema } from "~/components/OrganizationSchema";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { AartiTime, setKundalAartiTime, setVadodaraAartiTime } from "~/lib/store/slice/contactSlice";
import { useAppDispatch } from "~/lib/store/store";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { orgSchemaProps } from "~/utils/organizationSchema";
import { ApiService } from "~/utils/service";

type LoaderData = {
  title: string;
  kundalAartiTime: AartiTime;
  vadodaraAartiTime: AartiTime;
};

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("contactUs", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang || LANG.english;
  const apiService = new ApiService(lang);
  const kundalAartiTime: AartiTime = await apiService.getKundalAartiTime();
  const vadodaraAartiTime: AartiTime = await apiService.getVadodaraAartiTime();
  const DATA = {
    title: lang === LANG.gujarati ? "સંપર્ક કરો" : "Contact Us",
    kundalAartiTime,
    vadodaraAartiTime,
  };
  const etag = computeEtagFromData(DATA);

  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.CONTACT_US.HEADERS, ETag: etag },
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "Contact Us", path: "/contact-us" },
  ],
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

function ContactUs() {
  const dispatch = useAppDispatch();
  const { kundalAartiTime, vadodaraAartiTime } = useLoaderData<LoaderData>();
  dispatch(setKundalAartiTime(kundalAartiTime));
  dispatch(setVadodaraAartiTime(vadodaraAartiTime));

  return (
    <div className="w-full min-h-screen bg-primary">
      {/* Organization schema injected globally once */}
      <OrganizationSchema {...orgSchemaProps} />

      {/* Contact Section */}
      <ContactSection />

      {/* Temple Locations Section */}
      <TempleLocations />
    </div>
  );
}

export default ContactUs;
